/**
 * Logo Listing Grid module.
 * Handles filter dropdown toggle functionality without filtering.
 */

export const initLogoListingGrid = () => {
	const grids = document.querySelectorAll('.logo-listing-grid');
	if (!grids.length) return;

	grids.forEach((grid) => {
		const dropdowns = grid.querySelectorAll('.llg-filter-dropdown');
		const gridContainer = grid.querySelector('.llg-grid');
		let currentAbortController = null;

		// 1. Setup Dropdown Toggles
		dropdowns.forEach((dropdown) => {
			const btn = dropdown.querySelector('.llg-filter-btn');
			const menu = dropdown.querySelector('.llg-filter-menu');

			if (!btn || !menu) return;
			if (!btn.dataset.llgBound) {
				btn.dataset.llgBound = 'true';

				btn.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();

					const isOpen = menu.classList.contains('is-open');

					// Close all other open dropdowns within this grid
					dropdowns.forEach((otherDropdown) => {
						if (otherDropdown !== dropdown) {
							const otherBtn = otherDropdown.querySelector('.llg-filter-btn');
							const otherMenu = otherDropdown.querySelector('.llg-filter-menu');
							if (otherBtn) {
								otherBtn.classList.remove('is-active');
								otherBtn.setAttribute('aria-expanded', 'false');
							}
							if (otherMenu) {
								otherMenu.classList.remove('is-open');
								otherMenu.setAttribute('aria-hidden', 'true');
							}
						}
					});

					// Toggle current dropdown
					if (isOpen) {
						btn.classList.remove('is-active');
						btn.setAttribute('aria-expanded', 'false');
						menu.classList.remove('is-open');
						menu.setAttribute('aria-hidden', 'true');
					} else {
						btn.classList.add('is-active');
						btn.setAttribute('aria-expanded', 'true');
						menu.classList.add('is-open');
						menu.setAttribute('aria-hidden', 'false');
					}
				});

				// Prevent click inside menu from closing the dropdown immediately
				menu.addEventListener('click', (e) => {
					e.stopPropagation();
				});
			}
		});

		// 2. Setup Filter Checkboxes & AJAX
		if (grid.dataset.llgFilterBound) return;
		grid.dataset.llgFilterBound = 'true';

		const checkboxes = grid.querySelectorAll('.llg-filter-menu input[type="checkbox"]');
		if (!checkboxes.length || !gridContainer) return;

		// Helper function to update dropdown labels when options are selected
		const updateDropdownState = () => {
			dropdowns.forEach((dropdown) => {
				const btn = dropdown.querySelector('.llg-filter-btn');
				if (!btn) return;

				const checkedInputs = dropdown.querySelectorAll('input[type="checkbox"]:checked');
				btn.classList.toggle('has-selection', checkedInputs.length > 0);
			});
		};

		const gridWrap = grid.querySelector('.llg-grid-wrap') || (gridContainer ? gridContainer.parentElement : null);
		if (gridWrap && gridContainer && !gridWrap.querySelector('.llg-loader')) {
			const loaderEl = document.createElement('div');
			loaderEl.className = 'llg-loader';
			loaderEl.setAttribute('aria-hidden', 'true');
			loaderEl.innerHTML = '<div class="llg-spinner" role="status"></div>';
			gridWrap.insertBefore(loaderEl, gridContainer);
		}

		const performFilter = () => {
			updateDropdownState();

			// Abort any pending filter request
			if (currentAbortController) {
				currentAbortController.abort();
			}
			currentAbortController = new AbortController();

			const formData = new FormData();
			formData.append('action', 'llg_filter_logos');

			const nonce = grid.dataset.nonce || (typeof window !== 'undefined' && window.custom_ajax_object ? window.custom_ajax_object.nonce : '');
			formData.append('nonce', nonce);

			const postType = grid.dataset.postType || 'partners';
			formData.append('post_type', postType);

			const postsPerPage = grid.dataset.postsPerPage || '16';
			formData.append('posts_per_page', postsPerPage);

			const businessLinePreset = grid.dataset.businessLine || '';
			formData.append('business_line_preset', businessLinePreset);

			// Append selected taxonomy terms
			checkboxes.forEach((cb) => {
				if (cb.checked && cb.name && cb.value) {
					formData.append(cb.name, cb.value);
				}
			});

			const ajaxUrl = grid.dataset.ajaxUrl || (typeof window !== 'undefined' && window.custom_ajax_object ? window.custom_ajax_object.ajax_url : '/wp-admin/admin-ajax.php');

			const loaderEl = grid.querySelector('.llg-loader');
			const startTime = Date.now();
			const minLoadingTime = 300;

			grid.classList.add('is-loading');
			if (gridWrap) {
				gridWrap.classList.add('is-loading');
			}
			if (gridContainer) {
				gridContainer.classList.add('is-loading');
			}
			if (loaderEl) {
				loaderEl.classList.add('is-active');
			}

			fetch(ajaxUrl, {
				method: 'POST',
				body: formData,
				signal: currentAbortController.signal,
			})
				.then((response) => {
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}
					return response.json();
				})
				.then((res) => {
					if (res && res.success && res.data && typeof res.data.html !== 'undefined') {
						if (gridContainer) {
							gridContainer.innerHTML = res.data.html;
						}
					}
				})
				.catch((err) => {
					if (err.name !== 'AbortError') {
						console.error('Logo Listing Grid filter error:', err);
					}
				})
				.finally(() => {
					const elapsed = Date.now() - startTime;
					const delay = Math.max(0, minLoadingTime - elapsed);

					setTimeout(() => {
						grid.classList.remove('is-loading');
						if (gridWrap) {
							gridWrap.classList.remove('is-loading');
						}
						if (gridContainer) {
							gridContainer.classList.remove('is-loading');
						}
						if (loaderEl) {
							loaderEl.classList.remove('is-active');
						}
					}, delay);
				});
		};

		checkboxes.forEach((checkbox) => {
			checkbox.addEventListener('change', performFilter);
		});
	});
};

// Global click outside listener to close all open dropdowns
if (typeof document !== 'undefined') {
	document.addEventListener('click', () => {
		const openMenus = document.querySelectorAll('.llg-filter-menu.is-open');
		const activeBtns = document.querySelectorAll('.llg-filter-btn.is-active');

		openMenus.forEach((menu) => {
			menu.classList.remove('is-open');
			menu.setAttribute('aria-hidden', 'true');
		});

		activeBtns.forEach((btn) => {
			btn.classList.remove('is-active');
			btn.setAttribute('aria-expanded', 'false');
		});
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			const openMenus = document.querySelectorAll('.llg-filter-menu.is-open');
			const activeBtns = document.querySelectorAll('.llg-filter-btn.is-active');

			openMenus.forEach((menu) => {
				menu.classList.remove('is-open');
				menu.setAttribute('aria-hidden', 'true');
			});

			activeBtns.forEach((btn) => {
				btn.classList.remove('is-active');
				btn.setAttribute('aria-expanded', 'false');
			});
		}
	});
}

// Self-initialise on DOMContentLoaded or immediately if DOM is already ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initLogoListingGrid);
} else {
	initLogoListingGrid();
}

// ACF Gutenberg preview hook
if (typeof window !== 'undefined' && window.acf) {
	window.acf.addAction('render_block_preview/type=logo-listing-grid', initLogoListingGrid);
}

export default initLogoListingGrid;
